import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * SummaryEvents
 *--------------------------------------------------------------------------
 *
 * Collection of events for functionality related to the order summary on
 * checkout pages.
 *
 */
export default class SummaryEvents {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Summary container element
	 *
	 * @var Element
	 */
	containerElement;

	/**
	 * Summary toggle control element
	 *
	 * @var Element
	 */
	controlElement;

	/**
	 * Summary open class name
	 *
	 * @var String
	 */
	openClass;

	/**
	 * Class selectors object
	 *
	 * @var Object
	 */
	selectors;

	/**
	 * Create a new SummaryEvents object
	 *
	 * @param	Element	containerElement			Summary container element. Defaults to '.g-checkout__order-summary--collapsible'.
	 * @param	Element	controlElement				Summary toggle control element. Defaults to '#js-order-summary-content-control'.
	 * @param	String	marketingFormFieldSelector	Selector for the marketing form input field containers. Defaults to '.g-checkout__order-summary-promo-form-fields'.
	 * @param	String	marketingFormSelector		Selector for marketing forms (ie coupon form, gift cert form, etc.). Defaults to '.g-checkout__order-summary-promo-form'.
	 * @param	String	openClass					Summary is open identifier class. Defaults to 'is-open'.
	 *
	 */
	constructor ({
		containerElement = '.g-checkout__order-summary--collapsible',
		controlElement = '#js-order-summary-content-control',
		marketingFormFieldSelector = '.g-checkout__order-summary-promo-form-fields',
		marketingFormSelector = '.g-checkout__order-summary-promo-form',
		openClass = 'is-open'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.containerElement = this.#validator.isElement('containerElement', containerElement);

		this.controlElement = this.#validator.isElement('controlElement', controlElement, {container: this.containerElement});

		this.openClass = this.#validator.isString('openClass', openClass);

		this.selectors = {
			marketingFormFieldSelector: this.#validator.isString('marketingFormFieldSelector', marketingFormFieldSelector),
			marketingFormSelector: this.#validator.isString('marketingFormSelector', marketingFormSelector)
		};

		this.bindEvents();
		this.initMarketingFormsValidation();
	}

	bindEvents () {
		this.toggleSummary();
	}

	toggleSummary () {
		const self = this;

		const toggle = function (event) {
			event.preventDefault();

			if (self.containerElement.classList.contains(self.openClass)) {
				self.hideSummary();
			} else {
				self.showSummary();
			}
		};

		const showHideSummary = function (mediaQueryList) {
			if (mediaQueryList.matches) {
				self.hideSummary();
				self.controlElement.removeAttribute('aria-disabled');
				self.controlElement.addEventListener('click', toggle);
			} else {
				self.controlElement.removeEventListener('click', toggle);
				self.controlElement.setAttribute('aria-disabled', 'true');
				self.controlElement.setAttribute('aria-expanded', 'true');
				self.containerElement.classList.remove(self.openClass);
			}
		};

		const mediaQueryList = window.matchMedia('(max-width: 59.9375em)');

		showHideSummary(mediaQueryList);
		mediaQueryList.addListener(showHideSummary);
	}

	showSummary () {
		this.containerElement.classList.add(this.openClass);
		this.controlElement.setAttribute('aria-expanded', 'true');
	}

	hideSummary () {
		this.containerElement.classList.remove(this.openClass);
		this.controlElement.setAttribute('aria-expanded', 'false');
	}

	async initMarketingFormsValidation () {
		const marketingForms = this.containerElement.querySelectorAll(this.selectors.marketingFormSelector);

		if (!marketingForms) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const form of marketingForms) {
			new HTMLFormValidator({
				errorInsertPosition: 'afterend',
				fieldContainer: this.selectors.marketingFormFieldSelector,
				formElement: form
			});
		}
	}

}
